// eslint-disable-next-line import/no-extraneous-dependencies
import {
  AvConnection,
  AvConnectionSignals,
  AvContext,
  AvMonitoringEventTypeType,
  AvServiceInfo,
  AvSubsystemAvServiceType,
  ConnectionStates,
  StreamStates,
  avSelectors,
} from "@minervaproject/avsubsystem";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useStore } from "react-redux";

import { ForumAPIContext } from "contexts/forum_api_context";
import { useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useUserProperties } from "hooks/use_user_properties";
import { StoreType } from "pubsub/client";
import { setBandwidthModeForUser, setUserIsPerformingAvCheck } from "shared/reducers/classroom";
import {
  AvServiceType,
  CapabilityType,
  ClassroomInfoType,
  LocalModalContentType,
} from "shared/types";
import { BandwidthModeType } from "shared/types/user";
import { classroomSelectors } from "store";
import { showLocalModal } from "store/reducers/local";
import { createMonitoringEvent } from "store/reducers/monitoring";
import { selectEffectivePttMode } from "store/selectors/classroom";
import {
  getAvBandwidthModeFromUserBandwidthMode,
  getAvServiceFromAvServiceType,
  getDailyToken,
  getLicodeToken,
  getMonitoringEventTypefromAvType,
  getOpentokToken,
} from "utils/av_utils";

// Used by Daily and Licode
// For Daily the roomId has to be under 26 chars ("minerva-project"+roomID under 41 chars)
// https://docs.daily.co/reference/rest-api/rooms/set-room-config
const generateRoomId = (classInfo: ClassroomInfoType) => {
  return `${classInfo.org.id}-${classInfo.id}-${classInfo.date}`.slice(0, 25);
};

function AvContextProvider({ children, userId }: { children: React.ReactNode; userId: string }) {
  const avService = useAppSelector(classroomSelectors.selectAvService()) ?? AvServiceType.Licode;
  const opentokSessionId = useAppSelector(classroomSelectors.selectOpentokSessionId()) ?? "";
  const licodeServer = useAppSelector(classroomSelectors.selectLicodeServer());
  const store = useStore() as StoreType;
  const [avConnection, setAvSubsystem] = useState<AvConnection | null>(null);
  const dispatch = useAppDispatch();
  const isVisibleInClass = useCapability(CapabilityType.IS_VISIBLE_IN_CLASS, { userId });
  const isAvEnabled = useAppSelector(classroomSelectors.selectCurrentUserIsAvEnabled());
  const user = useUserProperties(userId, ["bandwidth_mode", "is_performing_av_check"]);
  const isPttMode = useAppSelector(selectEffectivePttMode(userId));
  const classInfo = useAppSelector(classroomSelectors.selectClassInfo());
  const roomId = classInfo ? generateRoomId(classInfo) : "error";
  const canEnableAudioOnly = useAppSelector(
    classroomSelectors.selectCurrentUserCanEnableAudioOnly(),
  );
  const forumApi = useContext(ForumAPIContext);

  const [isAvInfoValidated, setIsAvInfoValidated] = useState(false);

  const validateAvServer = useCallback(async () => {
    if (avService !== AvServiceType.Licode) {
      window.flightLogger.log(
        `[AvContextProvider] In validateAvServer: not Licode, no request needed.`,
      );
      setIsAvInfoValidated(true);
      return;
    }
    if (!licodeServer) {
      window.flightLogger.log(
        `[AvContextProvider] In validateAvServer: No Licode server present, requesting one`,
      );
      await forumApi.post(
        "api/v1/licode/get-licode-server",
        JSON.stringify({
          class_id: classInfo.id,
          force: false,
        }),
      );
      window.flightLogger.log(`[AvContextProvider] Successfully requested Licode server.`);
    } else {
      window.flightLogger.log(
        `[AvContextProvider] In validateAvServer: Licode server present, no request needed.`,
      );
    }
    setIsAvInfoValidated(true);
  }, [avService, classInfo.id, forumApi, licodeServer]);

  const emitMonitoringEvent = useCallback(
    (monitInfo: { type: AvMonitoringEventTypeType; userId: string; contextData: any }) => {
      const monitoringEventType = getMonitoringEventTypefromAvType(monitInfo.type);

      window.flightLogger.log(
        `[AvContextProvider] Emitting monitoring from A/V subsystem event: ${monitInfo.type}, monitoringEventType: ${monitoringEventType}, userId: ${monitInfo.userId}`,
      );
      store.dispatch(
        createMonitoringEvent({
          type: monitoringEventType,
          userId: monitInfo.userId,
          contextData: monitInfo.contextData,
        }),
      );
    },
    [store],
  );

  const getAvServiceToken = useCallback(
    async (avServiceInfo: AvServiceInfo) => {
      if (!forumApi) {
        return { token: "", apiKey: "" };
      }
      window.flightLogger.log(
        `[AvContextProvider] Enter getAvServiceToken, avService: ${avServiceInfo.avService}, licodeServer: ${avServiceInfo.licodeServer}, userId: ${userId}, roomId: ${avServiceInfo.roomId}`,
      );
      switch (avServiceInfo.avService) {
        case AvSubsystemAvServiceType.Licode:
          if (!avServiceInfo.licodeServer) {
            return { token: "", apiKey: "" };
          }
          return getLicodeToken(forumApi, avServiceInfo.roomId, userId, avServiceInfo.licodeServer);
        case AvSubsystemAvServiceType.Daily:
          return getDailyToken(forumApi, userId, avServiceInfo.roomId);
        case AvSubsystemAvServiceType.Opentok:
          return getOpentokToken(forumApi, avServiceInfo.opentokSessionId);
        default:
          // Fake
          return { token: "fake", apiKey: "fake" };
      }
    },
    [forumApi, userId],
  );

  const createAvConnection = useCallback(
    (
      avServiceInfo: AvServiceInfo,
      userIdForConnection: string,
      avStore: StoreType,
      bandwidthMode: BandwidthModeType,
    ) => {
      const newAvConnection = new AvConnection(
        avServiceInfo,
        userIdForConnection,
        avStore,
        getAvBandwidthModeFromUserBandwidthMode(bandwidthMode),
        getAvServiceToken,
      );
      newAvConnection.on(AvConnectionSignals.MONITORING_EVENT, emitMonitoringEvent);
      return newAvConnection;
    },
    [emitMonitoringEvent, getAvServiceToken],
  );

  // cleanup useEffect
  useEffect(() => {
    return () => {
      if (avConnection) {
        window.flightLogger.log(
          `[AvContextProvider] Component unmounted, cleaning up AvConnection.`,
        );
        avConnection.close();
        setAvSubsystem(null);
      }
    };
  }, [avConnection]);

  const bandwidthModeAfterConstraints =
    user?.bandwidth_mode === BandwidthModeType.AudioOnlyForced
      ? BandwidthModeType.AudioOnly
      : !canEnableAudioOnly && user?.bandwidth_mode === BandwidthModeType.AudioOnly
        ? BandwidthModeType.AutomaticBandwidth
        : user?.bandwidth_mode || BandwidthModeType.AutomaticBandwidth;

  // Update user.bandwidth_mode according to the decided bandwidth mode avoiding circular dependencies
  useEffect(() => {
    if (
      user?.bandwidth_mode &&
      user.bandwidth_mode !== BandwidthModeType.AudioOnlyForced &&
      user.bandwidth_mode !== bandwidthModeAfterConstraints
    ) {
      dispatch(setBandwidthModeForUser({ userId, bandwidthMode: bandwidthModeAfterConstraints }));
    }
  }, [bandwidthModeAfterConstraints, dispatch, userId, user?.bandwidth_mode]);

  // Make sure we validate the av info when it changes
  useEffect(() => {
    window.flightLogger.log(`[AvContextProvider] Av info changed, marking it to be validated.`);
    setIsAvInfoValidated(false);
  }, [avService, licodeServer]);

  useEffect(() => {
    if (isAvEnabled) {
      if (!isAvInfoValidated) {
        window.flightLogger.log(`[AvContextProvider] Validating av info.`);
        validateAvServer();
        return;
      }
      if (avService === AvServiceType.Licode && !licodeServer) {
        return;
      }
      if (!avConnection) {
        setAvSubsystem(
          createAvConnection(
            {
              avService: getAvServiceFromAvServiceType(avService),
              licodeServer,
              opentokSessionId,
              roomId,
            },
            userId,
            store,
            bandwidthModeAfterConstraints,
          ),
        );
        dispatch(setUserIsPerformingAvCheck({ userId, isPerformingAvCheck: true }));
        dispatch(showLocalModal({ content: LocalModalContentType.PreclassCheck }));
      } else {
        avConnection.updateServiceInfo({
          avService: getAvServiceFromAvServiceType(avService),
          licodeServer,
          opentokSessionId,
          roomId,
        });
        avConnection.setBandwidthMode(
          getAvBandwidthModeFromUserBandwidthMode(bandwidthModeAfterConstraints),
        );
      }
    } else {
      dispatch(setUserIsPerformingAvCheck({ userId, isPerformingAvCheck: true }));
      dispatch(showLocalModal({ content: LocalModalContentType.PreclassCheck }));
    }

    return () => {
      window.flightLogger.log(`[AvContextProvider] Cleaning up AvConnection`);
    };
  }, [
    isAvEnabled,
    isVisibleInClass,
    userId,
    dispatch,
    store,
    avConnection,
    bandwidthModeAfterConstraints,
    roomId,
    createAvConnection,
    isAvInfoValidated,
    avService,
    licodeServer,
    validateAvServer,
    opentokSessionId,
  ]);

  // We will manage the published stream here instead of in the ParticipantAV component
  // isVisibleInClass is used to decide if the user should publish
  const STATE_NEEDS_ACTION = [StreamStates.initial, StreamStates.failed];
  const isPerformingAvCheck = user?.is_performing_av_check;

  const streamState = avSelectors.useStreamState(userId);

  const shouldPublish =
    isVisibleInClass &&
    isPerformingAvCheck === false &&
    (!streamState || STATE_NEEDS_ACTION.includes(streamState.state));

  const shouldUnpublish =
    isVisibleInClass &&
    isPerformingAvCheck &&
    streamState &&
    !STATE_NEEDS_ACTION.includes(streamState.state);

  const isConnectionReady = avSelectors.useConnectionState() === ConnectionStates.connected;

  useEffect(() => {
    if (avConnection && shouldPublish && isConnectionReady) {
      window.flightLogger.log(`[AvContextProvider] Requesting publish`);
      avConnection.publish(true, true);
      avConnection.setLocalAudio(!isPttMode);
    } else if (avConnection && shouldUnpublish && isConnectionReady) {
      window.flightLogger.log(`[AvContextProvider] Requesting unpublish`);
      avConnection.unpublish();
    }
  }, [shouldPublish, shouldUnpublish, isConnectionReady, avConnection, isPttMode]);

  // avConnection is null on initial renders; ! satisfies AvContext's non-null type while
  // keeping children mounted so non-AV UI renders immediately. Consumers that call
  // avConnection methods must guard against null. Proper fix: make AvContext nullable.
  return <AvContext.Provider value={avConnection!}>{children}</AvContext.Provider>;
}

export { AvContextProvider };
